import React, { useState, useMemo } from 'react';
import {
    Box, Text, Paper, Badge, ActionIcon, Tooltip, Modal, Button,
    Alert, Divider, Stack, Table, Group, Title
} from '@mantine/core';
import { LoadingState } from './LoadingState';
import {
    IconUsers,
    IconSearch,
    IconX,
    IconCopy,
    IconExternalLink
} from '@tabler/icons-react';
import { entityService } from '../../services/entityServiceSingleton';
import { EntityDetailDialog } from '../dialogs';
import { copyToClipboard } from '../../utils/clipboardUtils';
import { formatDateTime } from '../../utils/dateTimeUtils';

interface MembershipListViewerProps {
    memberships: any[];
    title: string;
    subtitle?: string;
    maxHeight?: number;
    showCount?: boolean;
    clickable?: boolean;
    onMembershipClick?: (membershipId: string) => void;
    onMembershipDoubleClick?: (membershipId: string) => void;
    enableSearch?: boolean;
    enableCopy?: boolean;
    enableDetails?: boolean;
    className?: string;
    sx?: any;
}

interface MembershipDetails {
    id: string;
    displayName?: string;
    description?: string;
    entityType?: string;
    type?: string;
    memberCount?: number;
    created?: string;
    modified?: string;
    email?: string;
    members?: any[];
    membersLoading?: boolean;
    membersError?: string | null;
}

export const MembershipListViewer: React.FC<MembershipListViewerProps> = ({
    memberships = [],
    title,
    subtitle,
    maxHeight = 200,
    showCount = true,
    clickable = true,
    onMembershipClick,
    onMembershipDoubleClick,
    enableSearch = true,
    enableCopy = true,
    enableDetails = true,
    className,
    sx
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMembership, setSelectedMembership] = useState<string | null>(null);
    const [membershipDetails, setMembershipDetails] = useState<MembershipDetails | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState<string | null>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

    // State for member detail dialog
    const [memberDialogOpen, setMemberDialogOpen] = useState(false);
    const [memberEntity, setMemberEntity] = useState<any | null>(null);
    const [memberEntityType, setMemberEntityType] = useState<string>('');
    const [memberLoading, setMemberLoading] = useState(false);

    // Helper to get a display label from id/object
    const getLabel = (item: any): string => {
        if (item == null) return '';
        if (typeof item === 'string') return item;
        if (typeof item === 'object') {
            return item.DisplayName || item.displayName || item.Name || item.Id || item.id || JSON.stringify(item);
        }
        return String(item);
    };

    // Filter memberships based on search term
    const filteredMemberships = useMemo(() => {
        if (!searchTerm.trim()) return memberships;
        const q = searchTerm.toLowerCase();
        return memberships.filter(m => getLabel(m).toLowerCase().includes(q));
    }, [memberships, searchTerm]);

    // Handle membership click
    const handleMembershipClick = (membershipId: string) => {
        if (onMembershipClick) {
            onMembershipClick(membershipId);
        } else if (enableDetails) {
            setSelectedMembership(membershipId);
            fetchMembershipDetails(membershipId);
        }
    };

    // Handle membership double click
    const handleMembershipDoubleClick = (membershipId: string) => {
        if (onMembershipDoubleClick) {
            onMembershipDoubleClick(membershipId);
        }
    };

    // Handle member click - open in read-only dialog
    const handleMemberClick = async (memberId: string) => {
        if (!memberId) return;

        setMemberLoading(true);
        setMemberDialogOpen(true);
        setMemberEntityType('Person'); // Members are typically Person entities

        try {
            // Try to fetch as Person first, but we might need to determine the actual type
            // For now, assume it's a Person entity
            const entity = await entityService.getEntityById(memberId, 'Person', true);
            setMemberEntity(entity);
            // Update entity type if we can determine it from the entity
            if (entity?.EntityType) {
                setMemberEntityType(entity.EntityType);
            }
        } catch (error) {
            console.error('Failed to load member entity:', error);
            setMemberEntity(null);
        } finally {
            setMemberLoading(false);
        }
    };

    // Fetch membership details
    const fetchMembershipDetails = async (membershipId: string) => {
        setDetailsLoading(true);
        setDetailsError(null);
        setMembershipDetails(null);

        try {
            let membershipData: any = null;
            let entityType = 'Unknown';

            // Try to fetch as a Group first
            try {
                membershipData = await entityService.getEntityById(membershipId, 'Group');
                entityType = 'Group';
            } catch (groupError) {
                // If not a Group, try as a Set
                try {
                    membershipData = await entityService.getEntityById(membershipId, 'Set');
                    entityType = 'Set';
                } catch (setError) {
                    // If neither works, create a basic details object
                    setMembershipDetails({
                        id: membershipId,
                        displayName: membershipId,
                        entityType: 'Unknown'
                    });
                    setDetailsLoading(false);
                    setDetailsDialogOpen(true);
                    return;
                }
            }

            // Create initial membership details
            const details: MembershipDetails = {
                id: membershipId,
                displayName: membershipData.DisplayName || membershipData.Name,
                description: membershipData.Description,
                entityType: entityType,
                type: membershipData.Type || membershipData.type,
                memberCount: membershipData.MemberCount,
                created: membershipData.Created,
                modified: membershipData.Modified,
                email: membershipData.Email || membershipData.email || membershipData.Emails?.[0] || membershipData.emails?.[0],
                members: [],
                membersLoading: true,
                membersError: null
            };

            setMembershipDetails(details);
            setDetailsLoading(false);
            setDetailsDialogOpen(true);

            // Fetch members in the background
            try {
                const members = membershipData.Member || membershipData.Members || membershipData.members || [];
                setMembershipDetails(prev => prev ? {
                    ...prev,
                    members: members,
                    membersLoading: false
                } : null);
            } catch (memberError) {
                console.error('Error fetching members:', memberError);
                setMembershipDetails(prev => prev ? {
                    ...prev,
                    membersLoading: false,
                    membersError: 'Failed to fetch members'
                } : null);
            }

        } catch (error) {
            console.error('Error fetching membership details:', error);
            setDetailsError('Failed to fetch membership details');
            setDetailsLoading(false);
            setDetailsDialogOpen(true);
        }
    };

    // Copy membership ID to clipboard
    const handleCopyMembership = (membershipId: string) => {
        copyToClipboard(membershipId);
    };

    // Copy all memberships to clipboard
    const handleCopyAll = () => {
        copyToClipboard(memberships.join('\n'));
    };

    return (
        <Box className={className} style={sx}>
            {/* Header */}
            <Group justify="space-between" align="center" mb="md">
                <Box>
                    <Group gap="xs" align="center">
                        <IconUsers size={20} />
                        <Title order={5} m={0}>
                            {title}
                        </Title>
                        {showCount && (
                            <Badge variant="outline" size="sm">
                                {memberships.length}
                            </Badge>
                        )}
                    </Group>
                    {subtitle && (
                        <Text size="sm" c="dimmed">
                            {subtitle}
                        </Text>
                    )}
                </Box>

                {/* Action Buttons */}
                <Group gap="xs">
                    {enableCopy && (
                        <Tooltip label="Copy all memberships">
                            <ActionIcon size="sm" variant="subtle" onClick={handleCopyAll}>
                                <IconCopy size={16} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Group>
            </Group>

            {/* Search */}
            {enableSearch && memberships.length > 5 && (
                <Box mb="md">
                    <Paper withBorder p="xs">
                        <Group gap="xs" align="center" wrap="nowrap">
                        <IconSearch size={16} />
                        <input
                            type="text"
                            placeholder={`Search ${title.toLowerCase()}...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                border: 'none',
                                outline: 'none',
                                flex: 1,
                                fontSize: '0.875rem',
                                background: 'transparent'
                            }}
                        />
                        {searchTerm && (
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                onClick={() => setSearchTerm('')}
                            >
                                <IconX size={16} />
                            </ActionIcon>
                        )}
                        </Group>
                    </Paper>
                </Box>
            )}

            {/* Memberships List */}
            <Paper withBorder style={{ maxHeight, overflow: 'auto' }}>
                <Stack gap={0}>
                    {filteredMemberships.length === 0 ? (
                        <Box p="md">
                            <Text size="sm">{searchTerm ? "No memberships found" : "No memberships"}</Text>
                            {searchTerm && (
                                <Text size="xs" c="dimmed">Try adjusting your search term</Text>
                            )}
                        </Box>
                    ) : (
                        filteredMemberships.map((membership) => {
                            const membershipId = typeof membership === 'object' ? (membership.Id || membership.id || getLabel(membership)) : membership;
                            const label = getLabel(membership);
                            return (
                                <Group
                                    key={membershipId}
                                    component="div"
                                    justify="space-between"
                                    align="center"
                                    py={4}
                                    px="md"
                                    onClick={() => handleMembershipClick(membershipId)}
                                    onDoubleClick={() => handleMembershipDoubleClick(membershipId)}
                                    style={{
                                        cursor: (clickable || enableDetails) ? 'pointer' : 'default',
                                        opacity: (!clickable && !enableDetails) ? 0.6 : 1
                                    }}
                                >
                                    <Text size="sm" ff="monospace">
                                        {String(label)}
                                    </Text>
                                    {enableDetails && (
                                        <Group gap={4} onClick={(e) => e.stopPropagation()}>
                                            <Tooltip label="View details">
                                                <ActionIcon
                                                    size="sm"
                                                    variant="subtle"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleMembershipClick(membershipId);
                                                    }}
                                                >
                                                    <IconExternalLink size={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                            {enableCopy && (
                                                <Tooltip label="Copy ID">
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCopyMembership(membershipId);
                                                        }}
                                                    >
                                                        <IconCopy size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            )}
                                        </Group>
                                    )}
                                </Group>
                            );
                        })
                    )}
                </Stack>
            </Paper>

            {/* Membership Details Modal */}
            <Modal
                opened={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                size="sm"
                title={
                    <Group justify="space-between" align="center">
                        <Group gap="xs" align="center">
                            <IconUsers size={20} />
                            <Title order={5} m={0}>
                                Membership Details
                            </Title>
                        </Group>
                        <ActionIcon variant="subtle" size="sm" onClick={() => setDetailsDialogOpen(false)}>
                            <IconX size={16} />
                        </ActionIcon>
                    </Group>
                }
            >
                {detailsLoading && (
                    <LoadingState variant="centered" label="Loading membership details..." size={40} />
                )}

                {detailsError && (
                    <Alert color="red" mb="md">
                        {detailsError}
                    </Alert>
                )}

                {membershipDetails && (
                    <Box>
                        <Title order={5} mb="xs">
                            {membershipDetails.displayName}
                        </Title>

                        <Divider my="md" />

                        <Stack gap="md">
                            <Group align="flex-start" gap="xs">
                                <Box flex={1}>
                                    <Text size="xs" c="dimmed">
                                        ID
                                    </Text>
                                    <Text size="sm" ff="monospace">
                                        {membershipDetails.id}
                                    </Text>
                                </Box>
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    onClick={() => {
                                        copyToClipboard(membershipDetails.id);
                                    }}
                                >
                                    <IconCopy size={16} />
                                </ActionIcon>
                            </Group>

                            {membershipDetails.type && (
                                <Box>
                                    <Text size="xs" c="dimmed">
                                        Type
                                    </Text>
                                    <Badge variant="outline" size="sm" mt={4}>
                                        {membershipDetails.type}
                                    </Badge>
                                </Box>
                            )}

                            {membershipDetails.description && (
                                <Box>
                                    <Text size="xs" c="dimmed">
                                        Description
                                    </Text>
                                    <Text size="sm">
                                        {membershipDetails.description}
                                    </Text>
                                </Box>
                            )}

                            {membershipDetails.email && (
                                <Group align="flex-start" gap="xs">
                                    <Box flex={1}>
                                        <Text size="xs" c="dimmed">
                                            Email
                                        </Text>
                                        <Text size="sm">
                                            {membershipDetails.email}
                                        </Text>
                                    </Box>
                                    <ActionIcon
                                        size="sm"
                                        variant="subtle"
                                        onClick={() => {
                                            copyToClipboard(membershipDetails.email!);
                                        }}
                                    >
                                        <IconCopy size={16} />
                                    </ActionIcon>
                                </Group>
                            )}

                            {membershipDetails.memberCount !== undefined && (
                                <Box>
                                    <Text size="xs" c="dimmed">
                                        Member Count
                                    </Text>
                                    <Text size="sm">
                                        {membershipDetails.memberCount}
                                    </Text>
                                </Box>
                            )}

                            {membershipDetails.created && (
                                <Box>
                                    <Text size="xs" c="dimmed">
                                        Created
                                    </Text>
                                    <Text size="sm">
                                        {formatDateTime(membershipDetails.created)}
                                    </Text>
                                </Box>
                            )}

                            {membershipDetails.modified && (
                                <Box>
                                    <Text size="xs" c="dimmed">
                                        Modified
                                    </Text>
                                    <Text size="sm">
                                        {formatDateTime(membershipDetails.modified)}
                                    </Text>
                                </Box>
                            )}
                        </Stack>

                        {/* Members Section */}
                        {(membershipDetails.members || membershipDetails.membersLoading || membershipDetails.membersError) && (
                            <>
                                <Divider my="md" />
                                <Box>
                                    <Group gap="xs" align="center" mb="xs">
                                        <IconUsers size={20} />
                                        <Title order={5} m={0}>
                                            Members ({membershipDetails.members?.length || 0})
                                        </Title>
                                    </Group>

                                    {membershipDetails.membersLoading && (
                                        <LoadingState variant="centered" label="Loading members..." size={20} />
                                    )}

                                    {membershipDetails.membersError && (
                                        <Alert color="red" mb="md">
                                            {membershipDetails.membersError}
                                        </Alert>
                                    )}

                                    {membershipDetails.members && membershipDetails.members.length > 0 && (
                                        <Table.ScrollContainer minWidth={300}>
                                            <Table>
                                                <Table.Thead>
                                                    <Table.Tr>
                                                        <Table.Th fw={600}>Display Name</Table.Th>
                                                        <Table.Th fw={600}>EntityType</Table.Th>
                                                        <Table.Th fw={600} w={200} ta="right">Actions</Table.Th>
                                                    </Table.Tr>
                                                </Table.Thead>
                                                <Table.Tbody>
                                                    {membershipDetails.members.map((member) => {
                                                        const memberId = typeof member === 'object' ? (member.Id || member.id || getLabel(member)) : member;
                                                        const label = getLabel(member);
                                                        const memberEntityType = typeof member === 'object'
                                                            ? (member.EntityType || member.entityType || member.Type || member.type || 'N/A')
                                                            : 'N/A';

                                                        // Get additional member details for tooltip
                                                        const memberEmail = typeof member === 'object'
                                                            ? (member.Email || member.email || member.Emails?.[0] || member.emails?.[0] || '')
                                                            : '';
                                                        const memberDescription = typeof member === 'object'
                                                            ? (member.Description || member.description || '')
                                                            : '';
                                                        const memberAccountName = typeof member === 'object'
                                                            ? (member.AccountName || member.accountName || '')
                                                            : '';

                                                        // Build tooltip label with full details
                                                        const tooltipParts: string[] = [];
                                                        if (label) tooltipParts.push(`DisplayName: ${label}`);
                                                        if (memberEntityType && memberEntityType !== 'N/A') tooltipParts.push(`EntityType: ${memberEntityType}`);
                                                        if (memberEmail) tooltipParts.push(`Email: ${memberEmail}`);
                                                        if (memberAccountName) tooltipParts.push(`AccountName: ${memberAccountName}`);
                                                        if (memberDescription) tooltipParts.push(`Description: ${memberDescription}`);
                                                        if (memberId) tooltipParts.push(`Id: ${memberId}`);
                                                        const tooltipLabel = tooltipParts.join('\n');

                                                        // Determine backdrop color based on entity type
                                                        const getEntityTypeColor = (entityType: string) => {
                                                            const normalizedType = entityType?.toLowerCase() || '';
                                                            if (normalizedType.includes('student')) {
                                                                return 'rgba(33, 150, 243, 0.08)'; // Light blue
                                                            } else if (normalizedType.includes('employee') || normalizedType.includes('staff')) {
                                                                return 'rgba(76, 175, 80, 0.08)'; // Light green
                                                            } else if (normalizedType.includes('contractor')) {
                                                                return 'rgba(255, 152, 0, 0.08)'; // Light orange
                                                            } else if (normalizedType.includes('applicant')) {
                                                                return 'rgba(156, 39, 176, 0.08)'; // Light purple
                                                            } else if (normalizedType.includes('group')) {
                                                                return 'rgba(158, 158, 158, 0.08)'; // Light gray
                                                            } else {
                                                                return 'transparent'; // Default
                                                            }
                                                        };

                                                        return (
                                                            <Table.Tr
                                                                key={memberId}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    backgroundColor: getEntityTypeColor(memberEntityType)
                                                                }}
                                                                onClick={async () => {
                                                                    await handleMemberClick(memberId);
                                                                }}
                                                            >
                                                                <Table.Td>
                                                                    <Tooltip label={tooltipLabel} multiline>
                                                                        <Text size="sm" ff="monospace">
                                                                            {String(label)}
                                                                        </Text>
                                                                    </Tooltip>
                                                                </Table.Td>
                                                                <Table.Td>
                                                                    <Tooltip label={tooltipLabel} multiline>
                                                                        <Text size="sm">
                                                                            {memberEntityType}
                                                                        </Text>
                                                                    </Tooltip>
                                                                </Table.Td>
                                                                <Table.Td ta="right">
                                                                    <Group gap={4} justify="flex-end" wrap="nowrap">
                                                                        <Tooltip label="Copy Name to clipboard">
                                                                            <ActionIcon
                                                                                size="sm"
                                                                                variant="subtle"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    copyToClipboard(String(label));
                                                                                }}
                                                                            >
                                                                                <IconCopy size={16} />
                                                                            </ActionIcon>
                                                                        </Tooltip>
                                                                        <Tooltip label="Copy Id to clipboard">
                                                                            <ActionIcon
                                                                                size="sm"
                                                                                variant="subtle"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleCopyMembership(memberId);
                                                                                }}
                                                                            >
                                                                                <IconCopy size={16} />
                                                                            </ActionIcon>
                                                                        </Tooltip>
                                                                    </Group>
                                                                </Table.Td>
                                                            </Table.Tr>
                                                        );
                                                    })}
                                                </Table.Tbody>
                                            </Table>
                                        </Table.ScrollContainer>
                                    )}

                                    {membershipDetails.members && membershipDetails.members.length === 0 && (
                                        <Text size="sm" c="dimmed" py="md">
                                            No members found
                                        </Text>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                )}

                <Group justify="flex-end" mt="md">
                    <Button onClick={() => setDetailsDialogOpen(false)}>
                        Close
                    </Button>
                    {membershipDetails && enableCopy && (
                        <Button
                            variant="outline"
                            onClick={() => handleCopyMembership(membershipDetails.id)}
                        >
                            Copy ID
                        </Button>
                    )}
                </Group>
            </Modal>

            {/* Member detail dialog - read-only */}
            <EntityDetailDialog
                open={memberDialogOpen}
                entity={memberLoading ? null : memberEntity}
                entityType={memberEntityType}
                onClose={() => {
                    setMemberDialogOpen(false);
                    setMemberEntity(null);
                    setMemberEntityType('');
                }}
            />
        </Box>
    );
};
